import React, { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { MaterialReactTable } from "material-react-table";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { decodeHtml } from "../../../utils/decodeHtml";
import { openInAppTab } from "../../../utils/tabNavigation";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = (clientCount) => ({
  id: "centerTextPlugin",

  beforeDraw(chart) {
    const { ctx, width, height } = chart;

    ctx.save();

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = "bold 22px Arial";
    ctx.fillStyle = "#212529";
    ctx.fillText(clientCount, width / 2, height / 2 - 6);

    ctx.font = "14px Arial";
    ctx.fillStyle = "#6c757d";
    ctx.fillText("Members", width / 2, height / 2 + 14);

    ctx.restore();
  },
});

const RevenueRiskMember = ({
  chart_data = [],
  riskData = [],
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedRiskData, setSelectedRiskData] = useState([]);

  /*
   * Modal table columns
   */
  const modalColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 180,
        Cell: ({ cell, row }) => {
          const value = decodeHtml(cell.getValue() || "");

          return row.original.invoice_id ? (
            <Link
              to={`/order/view/${row.original.invoice_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                openInAppTab(
                  `/role/view/${row.original.role_id}`,
                );
              }}
            >
              {value}
            </Link>
          ) : (
            value
          );
        },
      },
      {
        accessorKey: "role_name",
        header: "Role",
        size: 180,
        Cell: ({ cell, row }) => {
          const value = decodeHtml(cell.getValue() || "");

          return row.original.role_id ? (
            <Link
              to={`/role/view/${row.original.role_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </Link>
          ) : (
            value
          );
        },
      },
      {
        accessorKey: "company_name",
        header: "Client",
        size: 180,
      },
      {
        accessorKey: "roleManager",
        header: "Role Manager",
        size: 150,
      },
      {
        accessorKey: "assignedTo",
        header: "Assigned To",
        size: 150,
      },
      {
        accessorKey: "entity",
        header: "Entity",
        size: 120,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
      },
      {
        accessorKey: "invoice_number",
        header: "Invoice Number",
        size: 150,
      },
      {
        accessorKey: "raised_date",
        header: "Raised On",
        size: 130,
      },
      {
        accessorKey: "issue_date",
        header: "Issued On",
        size: 130,
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 130,
      },
      {
        accessorKey: "major_industry",
        header: "Industry",
        size: 150,
      },
      {
        accessorKey: "region",
        header: "Region",
        size: 120,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 130,
      },
      {
        accessorKey: "revenue",
        header: "Total (INR)",
        size: 140,
      },
    ],
    []
  );

  /*
   * Return empty state after all hooks
   */
  if (!Array.isArray(chart_data) || chart_data.length === 0) {
    return <div>No data available</div>;
  }

  const COLOR_PALETTE = [
    "#EDC948",
    "#F28E2B",
    "#B07AA1",
    "#76B7B2",
    "#E15759",
    "#59A14F",
    "#4E79A7",
    "#FF9DA7",
    "#9C755F",
    "#BAB0AC",
  ];

  const TOP_N = 3;

  const sortedData = [...chart_data].sort(
    (a, b) =>
      Number(b.total_amount) - Number(a.total_amount)
  );

  const topClients = sortedData.slice(0, TOP_N);

  const others = sortedData.slice(TOP_N);

  const othersTotal = others.reduce(
    (sum, item) =>
      sum + Number(item.total_amount || 0),
    0
  );

  const chartClients =
    othersTotal > 0
      ? [
          ...topClients,
          {
            member_name: "Others",
            total_amount: othersTotal,
          },
        ]
      : topClients;

  const labels = chartClients.map(
    (item) => item.member_name
  );

  const values = chartClients.map((item) =>
    Number(Number(item.total_amount).toFixed(2))
  );

  const colors = labels.map(
    (_, index) =>
      COLOR_PALETTE[index % COLOR_PALETTE.length]
  );

  /*
   * Click Team Member
   *
   * Match:
   * Assigned To OR Role Manager
   */
  const handleMemberClick = (memberName) => {
    const selectedName = String(memberName)
      .trim()
      .toLowerCase();

    let filteredData = [];

    if (memberName === "Others") {
      const topMemberNames = topClients.map(
        (item) =>
          String(item.member_name)
            .trim()
            .toLowerCase()
      );

      filteredData = riskData.filter((item) => {
        const assignedTo = String(
          item.assignedTo ||
            item.assigned_to ||
            item.bdBy ||
            ""
        )
          .trim()
          .toLowerCase();

        const roleManager = String(
          item.roleManager ||
            item.role_manager ||
            item.bdBy ||
            ""
        )
          .trim()
          .toLowerCase();

        return (
          !topMemberNames.includes(assignedTo) &&
          !topMemberNames.includes(roleManager)
        );
      });
    } else {
      filteredData = riskData.filter((item) => {
        const assignedTo = String(
          item.assignedTo ||
            item.assigned_to ||
            ""
        )
          .trim()
          .toLowerCase();

        const roleManager = String(
          item.roleManager ||
            item.role_manager ||
            ""
        )
          .trim()
          .toLowerCase();

        return (
          assignedTo === selectedName ||
          roleManager === selectedName
        );
      });
    }

    setSelectedMember(memberName);
    setSelectedRiskData(filteredData);
    setShowModal(true);
  };

  /*
   * Doughnut data
   */
  const data = {
    labels,
    datasets: [
      {
        label: "Risk",
        data: values,
        backgroundColor: colors,
        borderWidth: 1,
      },
    ],
  };

  /*
   * Doughnut options
   */
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "55%",

    plugins: {
      legend: {
        display: false,
        position: "bottom",
      },

      tooltip: {
        callbacks: {
          label: (ctx) =>
            `₹ ${ctx.parsed.toFixed(2)} Cr`,
        },
      },
    },

    /*
     * Doughnut segment click
     */
    onClick: (event, elements) => {
      if (!elements.length) return;

      const index = elements[0].index;
      const memberName = labels[index];

      handleMemberClick(memberName);
    },
  };

  /*
   * Selected member total
   */
  const selectedMemberRevenue =
    selectedMember === "Others"
      ? othersTotal
      : chart_data.find(
          (item) =>
            String(item.member_name)
              .trim()
              .toLowerCase() ===
            String(selectedMember)
              .trim()
              .toLowerCase()
        )?.total_amount || 0;

  /*
   * Download Excel
   */
  const handleDownloadExcel = () => {
    if (!selectedRiskData.length) return;

    const excelData = selectedRiskData.map(
      (item) => ({
        Name: decodeHtml(item.name || ""),
        "Role Name": decodeHtml(
          item.role_name || ""
        ),
        Client: item.company_name || "",
        "Assigned To":
          item.assignedTo ||
          item.assigned_to ||
          "",
        "Role Manager":
          item.roleManager ||
          item.role_manager ||
          "",
        Entity: item.entity || "",
        Status: item.status || "",
        "Invoice Number":
          item.invoice_number || "",
        "Raised On":
          item.raised_date || "",
        "Issued On":
          item.issue_date || "",
        "Due Date":
          item.due_date || "",
        Industry:
          item.major_industry || "",
        Region: item.region || "",
        Amount: item.amount || "",
        "Total (INR)":
          item.revenue || "",
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(excelData);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Risk"
    );

    const safeMemberName =
      String(selectedMember)
        .replace(/[^a-zA-Z0-9]/g, "_")
        .substring(0, 50);

    XLSX.writeFile(
      workbook,
      `Revenue_Risk_${safeMemberName}.xlsx`
    );
  };

  return (
    <div className="d-flex flex-column h-100">

      {/* Doughnut */}
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: 170 }}
      >
        <div style={{ width: 170 }}>
          <Doughnut
            data={data}
            options={chartOptions}
            plugins={[
              centerTextPlugin(
                chart_data.length
              ),
            ]}
          />
        </div>
      </div>

      {/* Member list */}
      <div
        className="mt-2 flex-grow-1 overflow-auto"
        style={{ minHeight: 0 }}
      >
        <table className="table table-sm mb-0">
          <tbody>
            {chart_data.map((item, index) => (
              <tr
                key={index}
                onClick={() =>
                  handleMemberClick(
                    item.member_name
                  )
                }
                style={{
                  cursor: "pointer",
                }}
              >
                <td width="20">
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      display: "inline-block",
                      borderRadius: "50%",
                      backgroundColor:
                        index < 3
                          ? colors[index]
                          : "#BAB0AC",
                    }}
                  />
                </td>

                <td className="text-truncate">
                  {item.member_name}
                </td>

                <td className="text-end fw-semibold">
                  ₹{" "}
                  {Number(
                    item.total_amount
                  ).toFixed(2)}{" "}
                  Cr
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            backgroundColor:
              "rgba(0,0,0,0.5)",
          }}
        >
          <div
            className="modal-dialog modal-xl"
            style={{
              maxWidth: "95%",
            }}
          >
            <div className="modal-content">

              {/* Header */}
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    Revenue Risk
                  </h5>

                  <div className="text-muted">
                    Team Member:{" "}
                    <strong>
                      {selectedMember}
                    </strong>

                    <span className="ms-3 fw-semibold text-dark">
                      ₹{" "}
                      {Number(
                        selectedMemberRevenue
                      ).toFixed(2)}{" "}
                      Cr
                    </span>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={
                      handleDownloadExcel
                    }
                    disabled={
                      !selectedRiskData.length
                    }
                  >
                    <i className="fa fa-download me-1" />
                    Download Excel
                  </button>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setShowModal(false)
                    }
                  />
                </div>
              </div>

              {/* Body */}
              <div className="modal-body">

                <MaterialReactTable
                  columns={modalColumns}
                  data={selectedRiskData}
                  enableColumnFilters
                  enableSorting
                  enablePagination
                  enableGlobalFilter
                  enableDensityToggle={false}
                  enableFullScreenToggle={false}
                  muiTableBodyRowProps={({
                    row,
                  }) => ({
                    sx: {
                      cursor: row.original
                        .invoice_id
                        ? "pointer"
                        : "default",
                    },

                    onClick: () => {
                      if (
                        row.original.invoice_id
                      ) {
                        window.open(
                          `/order/view/${row.original.invoice_id}`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    },
                  })}
                  initialState={{
                      density: "compact",
                      pagination: {
                        pageIndex: 0,
                        pageSize: 100,
                      },
                    }}
                />

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueRiskMember;
